import { createBrowserRouter, RouteObject, Navigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout';
import { ProtectedRoute } from '@/components/auth';
import { RoleBasedDashboard } from '@/features/dashboard';
import { StudentsPage as Students, StudentDetail, StaffPage as Staff } from '@/features/people';
import { LeadsListPage, LeadDetailPage } from '@/features/leads';
import { Subjects, SubjectDetail } from '@/features/subjects';
import { Packages, PackageDetail, PackageFormPage } from '@/features/packages';
import { StudentEntitlementsTab } from '@/features/catalog';
import { CalendarPage, BranchesSettingsSection } from '@/features/calendar';
import {
  SessionAttendancePage,
  MyLeavesPage,
  StaffAttendanceSummaryPage,
  StudentAttendanceTab,
  LeavePolicySettingsSection,
} from '@/features/attendance';
import {
  StudentBookingsTab,
  RescheduleBookingButton,
  BookingPolicySettingsSection,
} from '@/features/bookings';
import { BookingsWithPickers } from '@/routes/bookingPickers';
import { SessionDetailWithRegister } from '@/routes/sessionRegister';
import {
  StudentBillingTab,
  BillingPolicySettingsSection,
  InvoicesListPage,
  InvoiceDetailPage,
  PaymentsListPage,
  StudentSpendInsight,
  StudentOverviewBilling,
} from '@/features/invoices';
import { OperationsDashboard } from '@/features/reports/OperationsDashboard';
import { StaffReports } from '@/features/reports/StaffReports';
import { BdeReport } from '@/features/reports/bde/BdeReport';
import { StudentGradesTab } from '@/features/reports';
import { TeamPage } from '@/features/team/TeamPage';
import { Settings } from '@/features/settings';
import { Login, Signup, ForcePasswordReset, PasswordResetGuard, OwnerAccountsSection } from '@/features/auth';
import {
  PortalLayout,
  MyStudentsPage,
  SessionsPage,
  InvoicesPage,
  BalancePage,
  RequestsPage,
  GradesPage,
} from '@/features/parent-portal';
import { RequestsHubWithLeaveApprovals } from '@/routes/requestsHub';
import { DataQualityPage } from '@/features/data-quality';
import { MessagesPage } from '@/features/messages';
import {
  MembershipsListPage,
  MembershipDetailPage,
  MembershipDLQPage,
} from '@/features/memberships';
import { ExpensesListPage } from '@/features/expenses/pages/ExpensesListPage';
import type { Role } from '@/types';
import { StudentCalendarTab } from '@/routes/calendarTabs';
import { StaffDetailWithTabs } from '@/routes/staffDetail';

// Roles that can manage students, classes, invoices, etc.
// owner is always allowed (bypassed in ProtectedRoute), listed here for clarity.
const ADMIN_ROLES: Role[] = ['owner', 'admin', 'manager'];
const TEACHER_ROLES: Role[] = ['owner', 'admin', 'manager', 'staff'];
// 11E.5: Settings is Owner/Admin-only per the nav spec — "Office manager: no
// Salary visibility, no Settings". Distinct from ADMIN_ROLES (which includes
// 'manager') because Settings holds fee/tenant config, not day-to-day ops.
const SETTINGS_ROLES: Role[] = ['owner', 'admin'];

const routes: RouteObject[] = [
  { path: '/login', element: <Login /> },
  { path: '/signup', element: <Signup /> },
  { path: '/force-password-reset', element: <ForcePasswordReset /> },

  // ── Parent portal (viewer / guardian role) ────────────────────────────
  {
    path: '/portal',
    element: (
      <ProtectedRoute requiredRoles={['viewer' as Role]}>
        <PortalLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/portal/students" replace /> },
      { path: 'students', element: <MyStudentsPage /> },
      { path: 'sessions', element: <SessionsPage /> },
      { path: 'invoices', element: <InvoicesPage /> },
      { path: 'balance',  element: <BalancePage /> },
      { path: 'grades',   element: <GradesPage /> },
      { path: 'requests', element: <RequestsPage /> },
    ],
  },

  // ── Admin / Staff app ─────────────────────────────────────────────────
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <PasswordResetGuard>
          <MainLayout />
        </PasswordResetGuard>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <RoleBasedDashboard /> },
      {
        path: 'students',
        element: <ProtectedRoute requiredRoles={TEACHER_ROLES} />,
        children: [
          { index: true, element: <Students /> },
          {
            path: ':id',
            element: (
              <StudentDetail
                renderEntitlementsTab={(studentId) => (
                  <StudentEntitlementsTab studentId={studentId} />
                )}
                renderBillingTab={(studentId) => (
                  <StudentBillingTab studentId={studentId} />
                )}
                renderBookingsTab={(studentId) => (
                  <StudentBookingsTab studentId={studentId} />
                )}
                renderAttendanceTab={(studentId) => (
                  <StudentAttendanceTab
                    studentId={studentId}
                    renderRescheduleAction={(row) => (
                      <RescheduleBookingButton
                        sessionId={row.session_id}
                        studentId={studentId}
                      />
                    )}
                  />
                )}
                renderCalendarTab={(studentId) => (
                  <StudentCalendarTab studentId={studentId} />
                )}
                renderGradesTab={(studentId) => (
                  <StudentGradesTab studentId={studentId} />
                )}
                renderInsights={(studentId) => (
                  <StudentSpendInsight studentId={studentId} />
                )}
                renderOverviewBilling={(studentId) => (
                  <StudentOverviewBilling studentId={studentId} />
                )}
              />
            ),
          },
        ],
      },
      {
        path: 'leads',
        element: <ProtectedRoute requiredRoles={ADMIN_ROLES} />,
        children: [
          { index: true, element: <LeadsListPage /> },
          { path: ':id', element: <LeadDetailPage /> },
        ],
      },
      {
        path: 'staff',
        element: <ProtectedRoute requiredRoles={ADMIN_ROLES} />,
        children: [
          { index: true, element: <Staff /> },
          // 11F.7 — subjects for the pay-rate grid are injected in the
          // composition root (People must not import Catalog).
          { path: ':id', element: <StaffDetailWithTabs /> },
        ],
      },
      {
        path: 'subjects',
        element: <ProtectedRoute requiredRoles={ADMIN_ROLES} />,
        children: [
          { index: true, element: <Subjects /> },
          { path: ':id', element: <SubjectDetail /> },
        ],
      },
      {
        path: 'packages',
        element: <ProtectedRoute requiredRoles={ADMIN_ROLES} />,
        children: [
          { index: true, element: <Packages /> },
          { path: 'new', element: <PackageFormPage /> },
          { path: ':id', element: <PackageDetail /> },
          { path: ':id/edit', element: <PackageFormPage /> },
        ],
      },
      {
        path: 'admin/catalog',
        element: <ProtectedRoute requiredRoles={ADMIN_ROLES} />,
        children: [{ index: true, element: <Navigate to="/subjects" replace /> }],
      },
      { path: 'calendar', element: <CalendarPage /> },
      {
        path: 'sessions/:id/attendance',
        element: <ProtectedRoute requiredRoles={TEACHER_ROLES} />,
        children: [{ index: true, element: <SessionAttendancePage /> }],
      },
      // 11E.7 — Classcard-parity session page. The Register is injected
      // here (composition root) because `features/calendar` may not import
      // `features/attendance`; see SessionDetailPage's docstring.
      {
        path: 'sessions/:id',
        element: <ProtectedRoute requiredRoles={TEACHER_ROLES} />,
        children: [{ index: true, element: <SessionDetailWithRegister /> }],
      },
      // 11E.5 — TEACHER_NAV's "My Leaves & Attendance" item. The real leave
      // application/approval flow is Prompt 11E.6; this route exists so the
      // nav link resolves to a page instead of an unmatched route.
      {
        path: 'my-leaves',
        element: <ProtectedRoute requiredRoles={TEACHER_ROLES} />,
        children: [{ index: true, element: <MyLeavesPage /> }],
      },
      { path: 'bookings', element: <BookingsWithPickers /> },
      // 11F.7 — Invoices list on the entity framework (Student-Name column,
      // search by name/number, real pagination) + full-page invoice detail.
      // Replaces the tab-and-modal Invoices surface at this route.
      {
        path: 'invoices',
        element: <ProtectedRoute requiredRoles={ADMIN_ROLES} />,
        children: [
          { index: true, element: <InvoicesListPage /> },
          { path: ':id', element: <InvoiceDetailPage /> },
        ],
      },
      // 11F.7 — tenant-wide payments list. Rows link back to the invoice
      // the payment settled (see PaymentsListPage's docstring).
      {
        path: 'payments',
        element: <ProtectedRoute requiredRoles={ADMIN_ROLES} />,
        children: [{ index: true, element: <PaymentsListPage /> }],
      },
      {
        path: 'reports',
        element: <ProtectedRoute requiredRoles={['owner', 'admin', 'manager', 'staff'] as Role[]} />,
        children: [{ index: true, element: <StaffReports /> }],
      },
      {
        path: 'reports/bde',
        element: <ProtectedRoute requiredRoles={['owner', 'admin', 'manager'] as Role[]} />,
        children: [{ index: true, element: <BdeReport /> }],
      },
      {
        path: 'operations-dashboard',
        element: <ProtectedRoute requiredRoles={['owner', 'admin', 'manager'] as Role[]} />,
        children: [{ index: true, element: <OperationsDashboard /> }],
      },
      {
        path: 'team',
        // Public page — no role guard (team is publicly visible)
        element: <TeamPage />,
      },
      {
        path: 'settings',
        element: <ProtectedRoute requiredRoles={SETTINGS_ROLES} />,
        children: [
          {
            index: true,
            element: (
              <Settings
                renderLeavePolicySection={() => <LeavePolicySettingsSection />}
                renderOwnerAccountsSection={() => <OwnerAccountsSection />}
                renderBookingPolicySection={() => <BookingPolicySettingsSection />}
                renderBillingPolicySection={() => <BillingPolicySettingsSection />}
                renderBranchesSection={() => <BranchesSettingsSection />}
              />
            ),
          },
        ],
      },
      {
        path: 'admin/messages',
        element: <ProtectedRoute requiredRoles={['owner', 'admin'] as Role[]} />,
        children: [{ index: true, element: <MessagesPage /> }],
      },
      // Prompt 11E.8 §6 — Expenses (office-manager quick action + admin list)
      {
        path: 'expenses',
        element: <ProtectedRoute requiredRoles={TEACHER_ROLES} />,
        children: [{ index: true, element: <ExpensesListPage /> }],
      },
      // Phase 10D — Memberships
      {
        path: 'admin/memberships',
        element: <ProtectedRoute requiredRoles={ADMIN_ROLES} />,
        children: [
          { index: true, element: <MembershipsListPage /> },
          { path: ':id', element: <MembershipDetailPage /> },
          { path: 'dlq', element: <MembershipDLQPage /> },
        ],
      },
      // 11E.5/11E.6/11F.6 — unified Requests hub: reschedule/cancel/unenroll
      // (own sub-tab, roles mirror the backend's _QUEUE_ROLES exactly —
      // backend/app/features/requests/router.py; a 'staff' caller is
      // further scoped server-side to their own students) + Leave approvals
      // (sub-tab exists only for owner/admin/manager — see
      // src/routes/requestsHub.tsx). TEACHER_ROLES here is the broader,
      // union set — the hub itself decides which sub-tabs a given role sees.
      {
        path: 'admin/requests',
        element: <ProtectedRoute requiredRoles={TEACHER_ROLES} />,
        children: [{ index: true, element: <RequestsHubWithLeaveApprovals /> }],
      },
      // NOTE: previously had a duplicate /admin/staff route gated to admin
      // only. Removed — every admin-only resource should live at its
      // canonical path with a tightened ProtectedRoute, not under a parallel
      // /admin/* hierarchy. Use requiredRoles={['admin']} on the canonical
      // route instead (see /staff above for the broader ADMIN_ROLES set).
      //
      // 11F.6 — old standalone Leave Approvals route now redirects into the
      // Requests hub's Leave approvals sub-tab. No role guard needed here —
      // the hub itself omits that tab for anyone outside ADMIN_ROLES.
      {
        path: 'admin/leaves',
        element: <Navigate to="/admin/requests?tab=leaves" replace />,
      },
      {
        path: 'admin/staff-attendance',
        element: <ProtectedRoute requiredRoles={ADMIN_ROLES} />,
        children: [{ index: true, element: <StaffAttendanceSummaryPage /> }],
      },
      // 11F.1 — data-quality abnormality queue. Roles mirror the backend's
      // QUEUE_ROLES exactly (backend/app/features/data_quality/config/rules.py).
      {
        path: 'admin/data-quality',
        element: <ProtectedRoute requiredRoles={['owner', 'admin']} />,
        children: [{ index: true, element: <DataQualityPage /> }],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
